import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Input } from '@angular/core';
import { withTabOrg } from '../../../../shared/navigation/org-tab';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription, from, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, mergeMap, switchMap, toArray } from 'rxjs/operators';

import {
  PhonebookContactVM,
  PhonebookListParams,
  PhonebookCountsVM,
  ContactType,
  ContactFormDialogData,
  DeleteConfirmDialogData,
  CreatePhonebookContactDTO,
  UpdatePhonebookContactDTO,
  PhonebookContactNoteVM,
} from '../../models';
import { PhonebookService } from '../../services/phonebook.service';
import { PhonebookSidebarService } from '../../services/phonebook-sidebar.service';
import { ToastService } from '../../services/toast.service';
import { CorporateFormsService } from '../../../services/corporate-forms.service';
import { CompaniesService } from '../../../../services/companies.service';
import { UserService } from '../../../../services/user.service';
import { UserCompany } from '../../../../models/user/user-companies-response-dto.model';
import { MicrosoftGraphService } from '../../../mail/services/microsoft-graph.service';
import { IFormContact } from '../../../models/IFormContact.interface';
import { ContactFormDialogComponent } from '../contact-form-dialog/contact-form-dialog.component';
import { DeleteConfirmDialogComponent } from '../delete-confirm-dialog/delete-confirm-dialog.component';
import { personMatches } from '../../../../shared/pipes/person-matches.pipe';

type SidebarView = 'list' | 'detail';

interface FilterTab {
  key: string;
  label: string;
  count?: number;
}

@Component({
    selector: 'app-phonebook-sidebar',
    templateUrl: './phonebook-sidebar.component.html',
    styleUrls: ['./phonebook-sidebar.component.scss'],
    standalone: false
})
export class PhonebookSidebarComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  /**
   * When true the phonebook renders as a standalone full-page view
   * (own browser tab) instead of the slide-in overlay sidebar.
   */
  @Input() pageMode = false;

  // Sidebar state
  isOpen = false;
  view: SidebarView = 'list';
  selectedContact: PhonebookContactVM | null = null;

  // Notes thread (loaded when a contact is selected)
  notes: PhonebookContactNoteVM[] = [];
  notesLoading = false;
  newNoteBody = '';
  addingNote = false;

  // List state
  contacts: PhonebookContactVM[] = [];
  total = 0;
  loading = false;
  loadingMore = false;

  // Form contacts (aggregated from submitted forms). Loaded once per open,
  // filtered client-side by search. Hidden when a specific filter tab is active.
  formContacts: PhonebookContactVM[] = [];
  private formContactsLoaded = false;

  /**
   * Colleagues in the organization, as contacts.
   *
   * A person is a person: whether they reach the directory as a client on a submitted form or as a
   * user of the system, someone looking for their number should find it in one place. They are
   * loaded separately because they come from a different source, then folded into the same list.
   */
  private colleagueContacts: PhonebookContactVM[] = [];

  /** The signed-in user's Outlook contacts, folded into the directory like the other sources. */
  private outlookContacts: PhonebookContactVM[] = [];

  /**
   * Photos already fetched, keyed by the address they were fetched for.
   *
   * Graph serves one photo per request, so fetching them for a whole list would be a request per
   * row. They are pulled for the contact being looked at instead, and kept — reopening someone is
   * then free, and the list shows a picture for anyone already viewed.
   */
  private readonly photoCache = new Map<string, string | null>();

  // Search & filters
  search = '';
  activeFilter = 'all';

  /** Person or organisation, applied on top of the contact-type tabs. */
  entityFilter: 'all' | 'individual' | 'business' = 'all';

  /** Whether colleagues from every organisation the user can reach are folded in. */
  crossOrgEmployees = false;
  loadingOtherOrgs = false;
  private otherOrgColleagues: PhonebookContactVM[] = [];
  private otherOrgsLoaded = false;

  readonly entityFilters: { key: 'all' | 'individual' | 'business'; label: string }[] = [
    { key: 'all', label: 'Everyone' },
    { key: 'individual', label: 'Individuals' },
    { key: 'business', label: 'Businesses' },
  ];
  page = 1;
  pageSize = 20;
  hasMore = false;

  // Counts
  counts: PhonebookCountsVM = {
    work: 0, client: 0, employee: 0, vendor: 0, consultant: 0,
    legal: 0, bank: 0, government: 0, familyMember: 0, general: 0, total: 0,
  };

  filters: FilterTab[] = [
    { key: 'all', label: 'All' },
    { key: 'Work', label: 'Work' },
    { key: 'Client', label: 'Client' },
    { key: 'Employee', label: 'Employee' },
    { key: 'Vendor', label: 'Vendor' },
    { key: 'Consultant', label: 'Consultant' },
    { key: 'Legal', label: 'Legal' },
    { key: 'Bank', label: 'Bank' },
    { key: 'Government', label: 'Gov' },
    { key: 'Family Member', label: 'Family' },
    { key: 'General', label: 'General' },
  ];

  // Search debounce
  private searchSubject = new Subject<string>();
  private subs: Subscription[] = [];

  constructor(
    private phonebookService: PhonebookService,
    private sidebarService: PhonebookSidebarService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private formsService: CorporateFormsService,
    private companiesService: CompaniesService,
    private userService: UserService,
    private graph: MicrosoftGraphService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    if (this.pageMode) {
      // Full-page mode: render immediately and load data, independent of
      // the sidebar toggle service (this lives in its own browser tab).
      this.isOpen = true;

      // Seed the box from ?search=, which is how the header's global search deep-links to a
      // colleague: an employee has no page of their own, so the link opens the directory already
      // narrowed to them. Set before loadContacts so the first request is the filtered one.
      const seed = this.route.snapshot.queryParamMap.get('search');
      if (seed) {
        this.search = seed;
      }

      this.loadContacts();
      this.loadCounts();
      this.loadFormContacts();
      this.loadColleagues();
      this.loadOutlookContacts();
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 200);
    } else {
      // Subscribe to sidebar open/close
      this.subs.push(
        this.sidebarService.isOpen$.subscribe((open) => {
          this.isOpen = open;
          if (open && this.contacts.length === 0) {
            this.loadContacts();
            this.loadCounts();
          }
          if (open && !this.formContactsLoaded) {
            this.loadFormContacts();
            this.loadColleagues();
            this.loadOutlookContacts();
          }
          if (open) {
            setTimeout(() => this.searchInput?.nativeElement?.focus(), 200);
          }
          if (!open) {
            this.view = 'list';
            this.selectedContact = null;
          }
        })
      );

      // Subscribe to selected contact
      this.subs.push(
        this.sidebarService.selectedContact$.subscribe((c) => {
          if (c) {
            this.selectedContact = c;
            this.view = 'detail';
            this.loadNotes(c);
          }
        })
      );
    }

    // Debounced search
    this.subs.push(
      this.searchSubject
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe((value) => {
          this.search = value;
          this.page = 1;
          this.contacts = [];
          this.loadContacts();
        })
    );

    // Global "new contact" requests (from the app header, any page).
    // This component is mounted app-wide, so the dialog opens everywhere.
    this.subs.push(
      this.sidebarService.createRequested$.subscribe(() => this.openCreateDialog())
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  // ═══════════════════════════════════
  // Data
  // ═══════════════════════════════════

  loadContacts(append = false): void {
    if (!append) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }

    const params: PhonebookListParams = {
      search: this.search || undefined,
      type: this.activeFilter !== 'all' ? (this.activeFilter as ContactType) : undefined,
      page: this.page,
      pageSize: this.pageSize,
      sortBy: 'firstName',
      sortDirection: 'asc',
    };

    this.phonebookService
      .list(params)
      .pipe(finalize(() => { this.loading = false; this.loadingMore = false; }))
      .subscribe({
        next: (res) => {
          if (append) {
            this.contacts = [...this.contacts, ...res.items];
          } else {
            this.contacts = res.items;
          }
          this.total = res.total;
          this.hasMore = this.contacts.length < res.total;
        },
        error: () => this.toastService.error('Failed to load contacts'),
      });
  }

  /**
   * Identity for de-duplication.
   *
   * Email first — it is the one field two records of the same person reliably agree on. Falling
   * back to name plus company catches the rest, at the cost of merging two genuinely different
   * people who share a name at one company. That trade is deliberate: the directory listing the
   * same person three times is the complaint, and a rare over-merge is visible and fixable where a
   * triplicate is just noise.
   */
  private identityKey(contact: PhonebookContactVM): string {
    const email = (contact.email || '').trim().toLowerCase();
    if (email) return `e:${email}`;

    const name = `${contact.firstName || ''} ${contact.lastName || ''}`
      .replace(/\s+/g, ' ').trim().toLowerCase();
    const company = (contact.company || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return `n:${name}|${company}`;
  }

  /**
   * Collapses records of the same person, keeping the fullest one.
   *
   * "Fullest" is simply how many contact fields are populated: a synthesized row carrying only a
   * name loses to a real phonebook entry with an email and two numbers, whichever order they
   * arrive in. Fields the winner is missing are filled from the loser rather than discarded, so
   * merging never costs information.
   */
  private dedupe(contacts: PhonebookContactVM[]): PhonebookContactVM[] {
    const filled = (c: PhonebookContactVM): number =>
      [c.email, c.mobilePhone, c.workPhone, c.company, c.jobTitle, c.address].filter(Boolean).length;

    const byIdentity = new Map<string, PhonebookContactVM>();

    for (const contact of contacts) {
      const key = this.identityKey(contact);
      const existing = byIdentity.get(key);
      if (!existing) {
        byIdentity.set(key, contact);
        continue;
      }

      const [winner, loser] = filled(contact) > filled(existing)
        ? [contact, existing]
        : [existing, contact];

      byIdentity.set(key, {
        ...winner,
        email: winner.email || loser.email,
        mobilePhone: winner.mobilePhone || loser.mobilePhone,
        workPhone: winner.workPhone || loser.workPhone,
        company: winner.company || loser.company,
        jobTitle: winner.jobTitle || loser.jobTitle,
      });
    }

    return [...byIdentity.values()];
  }

  /**
   * Pulls the signed-in user's Outlook contacts.
   *
   * Silent on every failure, including not being connected to Graph at all: the directory is
   * useful without Outlook in it, and a contacts panel is the wrong place to interrupt someone
   * with a consent prompt they did not ask for.
   */
  loadOutlookContacts(): void {
    if (!this.graph.isConnected()) return;

    this.graph.getOutlookContacts()
      .then(items => {
        this.outlookContacts = (items || [])
          .filter(c => c?.displayName || c?.emailAddresses?.length)
          .map((c): PhonebookContactVM => ({
            id: -2_000_000 - Math.abs(this.hashId(c.id)),
            firstName: c.givenName || (c.displayName || '').split(' ')[0] || '',
            lastName: c.surname || (c.displayName || '').split(' ').slice(1).join(' ') || null,
            email: c.emailAddresses?.[0]?.address || null,
            mobilePhone: c.mobilePhone || null,
            workPhone: c.businessPhones?.[0] || null,
            company: c.companyName || null,
            contactType: 'General',
            jobTitle: c.jobTitle || null,
            department: c.department || null,
            address: null, city: null, country: null, tic: null, vatNumber: null,
            notes: null, preferredContactMethods: null,
            leadRating: null, industry: null,
            ownerAzureObjectId: null, ownerName: null,
            introducerId: null, introducerName: null,
            linkedInUrl: null, website: null, facebook: null, instagram: null,
            createdAt: '', updatedAt: null,
            isFormContact: true,
            outlookContactId: c.id,
            derivedSource: 'outlook',
          }));
      })
      .catch(() => { this.outlookContacts = []; });
  }

  /** Stable numeric id from Outlook's string id, so synthesized rows keep distinct negative ids. */
  private hashId(value: string): number {
    let hash = 0;
    for (let i = 0; i < (value || '').length; i++) {
      hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
    }
    return hash;
  }

  /**
   * Fetches the profile picture for whoever is being looked at.
   *
   * By email against the directory, or by id for an Outlook contact. A miss is cached as well as a
   * hit — most people have no photo, and without that this would re-request on every open.
   */
  private loadPhotoFor(contact: PhonebookContactVM): void {
    if (!this.graph.isConnected()) return;

    const key = contact.outlookContactId || (contact.email || '').toLowerCase();
    if (!key) return;

    const cached = this.photoCache.get(key);
    if (cached !== undefined) {
      contact.photoUrl = cached;
      return;
    }

    const request = contact.outlookContactId
      ? this.graph.getOutlookContactPhoto(contact.outlookContactId)
      : this.graph.getPhotoFor(key);

    request.then(blob => {
      const url = blob ? URL.createObjectURL(blob) : null;
      this.photoCache.set(key, url);
      contact.photoUrl = url;
    }).catch(() => this.photoCache.set(key, null));
  }

  /**
   * Why a contact cannot be edited here. Each derived source has a different owner, and telling
   * someone their colleague is "managed via submitted forms" just sends them looking in the wrong
   * place.
   */
  readOnlyReason(contact: PhonebookContactVM): string {
    switch (contact.derivedSource) {
      case 'form': return 'Managed on the client\'s record';
      case 'colleague': return 'Managed in company settings';
      case 'outlook': return 'Managed in Outlook';
      default: return 'Read-only';
    }
  }

  /**
   * Whether a row is an organisation rather than a person.
   *
   * Client-form contacts carry formContactType, which says so outright. Phonebook entries do
   * not have such a flag — they are stored as a person with an associated company — so the only
   * signal for one standing in for a company is that its name *is* the company and there is no
   * surname. That covers rows like "Prime Ltd" and leaves genuine people alone.
   */
  isBusinessContact(contact: PhonebookContactVM): boolean {
    if (contact.formContactType) return contact.formContactType === 'Business';
    return !contact.lastName && !!contact.company && contact.company === contact.firstName;
  }

  matchesEntityFilter(contact: PhonebookContactVM): boolean {
    if (this.entityFilter === 'all') return true;
    const isBusiness = this.isBusinessContact(contact);
    return this.entityFilter === 'business' ? isBusiness : !isBusiness;
  }

  setEntityFilter(key: 'all' | 'individual' | 'business'): void {
    this.entityFilter = key;
  }

  /** True when there is somewhere to send the user to change this contact. */
  canOpenSource(contact: PhonebookContactVM): boolean {
    return contact.derivedSource === 'form' && !!contact.submittedFormId;
  }

  /** Opens the client record a derived contact belongs to, where its details are actually edited. */
  openContactSource(contact: PhonebookContactVM): void {
    if (!this.canOpenSource(contact)) return;
    window.open(withTabOrg(`/edit-form/${contact.submittedFormId}`), '_blank');
  }

  /**
   * One company user as a directory row.
   *
   * `orgName` is written into `company` rather than a new field: it is what the row already
   * shows under the name, so an employee borrowed from another organisation says which one
   * without the list needing a second layout.
   */
  private mapUserToContact(u: any, orgName: string | null, idOffset = 0): PhonebookContactVM {
    return {
      id: -1_000_000 - idOffset - u.id,
      firstName: u.firstName || (u.email || '').split('@')[0],
      lastName: u.lastName || null,
      email: u.email || null,
      mobilePhone: u.phoneNumber || null,
      workPhone: null,
      company: orgName,
      contactType: 'Employee',
      jobTitle: u.jobTitle || null,
      department: null,
      address: null,
      city: null,
      country: null,
      tic: null,
      vatNumber: null,
      notes: null,
      preferredContactMethods: null,
      leadRating: null,
      industry: null,
      ownerAzureObjectId: u.azureObjectId ?? null,
      ownerName: null,
      introducerId: null,
      introducerName: null,
      linkedInUrl: null,
      website: null,
      facebook: null,
      instagram: null,
      createdAt: '',
      updatedAt: null,
      isFormContact: true,
      formContactType: 'Individual',
      derivedSource: 'colleague',
    };
  }

  /**
   * Fold in colleagues from every OTHER organisation the user can reach.
   *
   * One request per company — there is no endpoint spanning them — so this is opt-in behind the
   * toggle rather than part of the initial load, runs three at a time, and each company catches
   * its own error: losing access to one organisation must not empty the list. Loaded once and
   * kept, because the answer does not change while the panel is open.
   *
   * A person who belongs to two organisations is still de-duplicated downstream — this widens
   * who appears, it does not list anyone twice.
   */
  toggleCrossOrgEmployees(): void {
    this.crossOrgEmployees = !this.crossOrgEmployees;
    if (!this.crossOrgEmployees || this.otherOrgsLoaded || this.loadingOtherOrgs) return;

    const currentCompanyId = this.companiesService.managedCompanyId;
    this.loadingOtherOrgs = true;

    this.userService.getUserCompanies().pipe(
      map(res => (res?.companies ?? []).filter(c => c.id && c.id !== currentCompanyId)),
      catchError(() => of([] as UserCompany[])),
      switchMap(companies => {
        if (!companies.length) return of([] as PhonebookContactVM[]);
        return from(companies).pipe(
          mergeMap(company => this.companiesService.getCompanyUsers(company.id).pipe(
            map((res: any) => {
              const users = res?.users ?? res ?? [];
              return users
                .filter((u: any) => !!u?.id && (u.firstName || u.lastName || u.email))
                .map((u: any) => this.mapUserToContact(u, company.name, company.id * 10_000));
            }),
            catchError(() => of([] as PhonebookContactVM[])),
          ), 3),
          toArray(),
          map(batches => batches.flat()),
        );
      }),
      finalize(() => { this.loadingOtherOrgs = false; }),
    ).subscribe({
      next: contacts => {
        this.otherOrgColleagues = contacts;
        this.otherOrgsLoaded = true;
        if (!contacts.length) this.toastService.info('No employees found in your other organisations.');
      },
      error: () => {
        this.otherOrgColleagues = [];
        this.toastService.error('Could not load employees from your other organisations.');
      },
    });
  }

  /** Colleagues, mapped onto the contact shape and marked read-only like other derived rows. */
  loadColleagues(): void {
    this.companiesService.getCompanyUsers().subscribe({
      next: (res: any) => {
        const users = res?.users ?? res ?? [];
        this.colleagueContacts = users
          .filter((u: any) => !!u?.id && (u.firstName || u.lastName || u.email))
          .map((u: any) => this.mapUserToContact(u, null));
      },
      // Additive, like form contacts: the directory still works without colleagues in it.
      error: () => { this.colleagueContacts = []; },
    });
  }

  loadFormContacts(): void {
    this.formsService.getFormContacts().subscribe({
      next: (items: IFormContact[]) => {
        this.formContacts = (items || [])
          .filter(fc => !!fc.submittedFormId && !!(fc.displayName || fc.company))
          .map<PhonebookContactVM>(fc => {
            const parts = (fc.displayName || '').trim().split(/\s+/);
            const firstName = parts[0] || (fc.displayName || fc.company || '').trim();
            const lastName = parts.length > 1 ? parts.slice(1).join(' ') : null;
            return {
              id: -fc.submittedFormId,
              firstName,
              lastName,
              email: this.normalizeFormContactEmail(fc.email),
              mobilePhone: this.normalizeFormContactPhone(fc.phoneNumber),
              workPhone: null,
              company: fc.company,
              contactType: 'Client',
              jobTitle: null,
              department: null,
              address: null,
              city: null,
              country: null,
              tic: null,
              vatNumber: null,
              notes: null,
              preferredContactMethods: null,
              leadRating: null,
              industry: null,
              ownerAzureObjectId: null,
              ownerName: null,
              introducerId: null,
              introducerName: null,
              linkedInUrl: null,
              website: null,
              facebook: null,
              instagram: null,
              createdAt: '',
              updatedAt: null,
              isFormContact: true,
              formContactType: fc.type,
              derivedSource: 'form',
              submittedFormId: fc.submittedFormId,
            };
          });
        this.formContactsLoaded = true;
      },
      error: () => {
        // Silent fail — form contacts are additive; phonebook still works without them.
        this.formContactsLoaded = true;
      },
    });
  }

  /**
   * Form contacts arrive with phoneNumber serialized as JSON:
   *   [{"code":"+357","phone":25750469}]
   * Flatten it to a readable "+357 25750469" string; accept plain strings too.
   */
  private normalizeFormContactPhone(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    if (trimmed[0] !== '[' && trimmed[0] !== '{') return trimmed;
    try {
      const parsed = JSON.parse(trimmed);
      const entries = Array.isArray(parsed) ? parsed : [parsed];
      const pieces = entries
        .map((e: any) => {
          const code = e?.code != null ? String(e.code).trim() : '';
          const phone = e?.phone != null ? String(e.phone).trim() : '';
          return [code, phone].filter(Boolean).join(' ').trim();
        })
        .filter((s: string) => s.length > 0);
      return pieces.length ? pieces.join(', ') : null;
    } catch {
      return trimmed;
    }
  }

  /** Some submitted-form answers serialize emails the same way (array of objects). */
  private normalizeFormContactEmail(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    if (trimmed[0] !== '[' && trimmed[0] !== '{') return trimmed;
    try {
      const parsed = JSON.parse(trimmed);
      const entries = Array.isArray(parsed) ? parsed : [parsed];
      const pieces = entries
        .map((e: any) => {
          if (typeof e === 'string') return e.trim();
          return (e?.email || e?.address || '').toString().trim();
        })
        .filter((s: string) => s.length > 0);
      return pieces.length ? pieces.join(', ') : null;
    } catch {
      return trimmed;
    }
  }

  /**
   * Derived contacts — clients from submitted forms, plus colleagues — filtered by the search term
   * and de-duplicated, both among themselves and against the phonebook's own entries.
   *
   * The two lists were previously rendered side by side untouched, so a client who also had a
   * phonebook card, or who appeared on two submitted forms, was listed two or three times over.
   */
  /**
   * The phonebook's own rows, after the Individuals / Businesses filter.
   *
   * Applied here rather than in the request because the discriminator is client-side: nothing
   * on the API marks a row as an organisation, so filtering server-side would need a field that
   * does not exist yet.
   */
  get visiblePhonebookContacts(): PhonebookContactVM[] {
    if (this.entityFilter === 'all') return this.contacts;
    return this.contacts.filter(c => this.matchesEntityFilter(c));
  }

  get visibleFormContacts(): PhonebookContactVM[] {
    if (this.activeFilter !== 'all') return [];

    const derived = this.dedupe([
      ...this.formContacts,
      ...this.colleagueContacts,

      ...(this.crossOrgEmployees ? this.otherOrgColleagues : []),
      ...this.outlookContacts,
    ]);

    // Anything the phonebook itself already lists wins: it is the editable record.
    const known = new Set(this.contacts.map(c => this.identityKey(c)));
    const unique = derived.filter(c => !known.has(this.identityKey(c)));

    const scoped = unique.filter(c => this.matchesEntityFilter(c));

    if (!this.search) return scoped;
    const q = this.search.toLowerCase();
    return scoped.filter(c =>
      personMatches(c, q) ||
      (c.company || '').toLowerCase().includes(q) ||
      (c.mobilePhone || '').toLowerCase().includes(q)
    );
  }

  loadCounts(): void {
    this.phonebookService.getCounts().subscribe({
      next: (counts) => {
        this.counts = counts;
        this.filters = [
          { key: 'all', label: 'All', count: counts.total },
          { key: 'Work', label: 'Work', count: counts.work },
          { key: 'Client', label: 'Client', count: counts.client },
          { key: 'Employee', label: 'Employee', count: counts.employee },
          { key: 'Vendor', label: 'Vendor', count: counts.vendor },
          { key: 'Consultant', label: 'Consultant', count: counts.consultant },
          { key: 'Legal', label: 'Legal', count: counts.legal },
          { key: 'Bank', label: 'Bank', count: counts.bank },
          { key: 'Government', label: 'Gov', count: counts.government },
          { key: 'Family Member', label: 'Family', count: counts.familyMember },
          { key: 'General', label: 'General', count: counts.general },
        ];
      },
    });
  }

  loadMore(): void {
    if (this.hasMore && !this.loadingMore) {
      this.page++;
      this.loadContacts(true);
    }
  }

  // ═══════════════════════════════════
  // Search & Filters
  // ═══════════════════════════════════

  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.search = '';
    this.searchSubject.next('');
    this.searchInput?.nativeElement?.focus();
  }

  setFilter(key: string): void {
    this.activeFilter = key;
    this.page = 1;
    this.contacts = [];
    this.loadContacts();
  }

  // ═══════════════════════════════════
  // Contact Selection
  // ═══════════════════════════════════

  selectContact(contact: PhonebookContactVM): void {
    this.selectedContact = contact;
    this.view = 'detail';
    this.loadNotes(contact);
    this.loadPhotoFor(contact);
  }

  backToList(): void {
    this.view = 'list';
    this.selectedContact = null;
    this.notes = [];
    this.newNoteBody = '';
  }

  // ═══════════════════════════════════
  // Notes thread
  // ═══════════════════════════════════

  private loadNotes(contact: PhonebookContactVM): void {
    this.notes = [];
    // Synthetic form-contact rows have negative ids — no notes backend.
    if (contact.isFormContact || contact.id < 0) return;

    this.notesLoading = true;
    this.phonebookService.listNotes(contact.id)
      .pipe(finalize(() => (this.notesLoading = false)))
      .subscribe({
        next: (notes) => (this.notes = notes),
        error: () => (this.notes = []),
      });
  }

  addNote(): void {
    const body = (this.newNoteBody || '').trim();
    if (!body || !this.selectedContact || this.selectedContact.isFormContact) return;

    this.addingNote = true;
    this.phonebookService.addNote(this.selectedContact.id, { body })
      .pipe(finalize(() => (this.addingNote = false)))
      .subscribe({
        next: (note) => {
          this.notes = [note, ...this.notes];
          this.newNoteBody = '';
          this.toastService.success('Note added');
        },
        error: () => this.toastService.error('Failed to add note'),
      });
  }

  deleteNote(note: PhonebookContactNoteVM): void {
    if (!this.selectedContact) return;
    this.phonebookService.deleteNote(this.selectedContact.id, note.id).subscribe({
      next: () => {
        this.notes = this.notes.filter((n) => n.id !== note.id);
      },
      error: () => this.toastService.error('Failed to delete note'),
    });
  }

  /** Human-friendly relative time ("just now", "3h ago", "yesterday", "Mar 12"). */
  formatNoteTime(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 172800) return 'yesterday';
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /**
   * True when this contact has an edit surface at all.
   *
   * Three kinds of row end up in this list and only two can be changed: a phonebook entry opens
   * the edit dialog, a client-form contact opens the client record that owns it, and colleagues
   * and Outlook contacts are owned elsewhere entirely.
   */
  canEditContact(contact: PhonebookContactVM): boolean {
    if (contact.isFormContact) return this.canOpenSource(contact);
    return !contact.derivedSource;
  }

  /** Send the user to wherever this contact is actually edited. */
  editContact(contact: PhonebookContactVM): void {
    if (contact.isFormContact) {
      if (this.canOpenSource(contact)) this.openContactSource(contact);
      else this.toastService.info(this.readOnlyReason(contact));
      return;
    }

    if (contact.derivedSource) {
      this.toastService.info(this.readOnlyReason(contact));
      return;
    }

    this.openEditDialog(contact);
  }

  // ─── Pick contact (for use in other pages) ───
  pickContact(contact: PhonebookContactVM): void {
    this.sidebarService.pickContact(contact);
    this.sidebarService.close();
    this.toastService.info(`Selected ${contact.firstName} ${contact.lastName || ''}`);
  }

  // ═══════════════════════════════════
  // CRUD Actions
  // ═══════════════════════════════════

  /**
   * Dialog config that adapts to the viewport: a centred card on desktop,
   * a bottom-anchored full-width sheet on phones (thumb-friendly, app-like).
   */
  private contactDialogConfig() {
    const isPhone = typeof window !== 'undefined'
      && window.matchMedia('(max-width: 600px)').matches;

    if (isPhone) {
      // A fixed-height bottom-sheet so the flex layout inside (sticky
      // header + scrollable body + sticky footer) actually works — with
      // `height: 'auto'` the dialog grows past the viewport on long tabs
      // and the Save button slips off-screen.
      return {
        width: '100vw',
        maxWidth: '100vw',
        height: '92vh',
        maxHeight: '92vh',
        position: { bottom: '0' },
        panelClass: ['bc-dialog-panel', 'bc-dialog-panel--sheet'],
        autoFocus: false,
      };
    }
    return {
      width: 'min(680px, 96vw)',
      maxWidth: '96vw',
      maxHeight: '90vh',
      panelClass: 'bc-dialog-panel',
      autoFocus: false,
    };
  }

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(ContactFormDialogComponent, {
      ...this.contactDialogConfig(),
      data: { mode: 'create' } as ContactFormDialogData,
    });

    dialogRef.afterClosed().subscribe((result: CreatePhonebookContactDTO | { openExisting: PhonebookContactVM } | null) => {
      // User clicked a "possible duplicate" in the banner — open that contact
      // for editing instead of creating a new one.
      if (result && (result as any).openExisting) {
        this.openEditDialog((result as any).openExisting);
        return;
      }
      if (result) {
        this.phonebookService.create(result as CreatePhonebookContactDTO).subscribe({
          next: () => {
            this.toastService.success('Contact created');
            this.page = 1;
            this.contacts = [];
            this.loadContacts();
            this.loadCounts();
          },
          error: () => this.toastService.error('Failed to create contact'),
        });
      }
    });
  }

  openEditDialog(contact: PhonebookContactVM): void {
    const dialogRef = this.dialog.open(ContactFormDialogComponent, {
      ...this.contactDialogConfig(),
      data: { contact, mode: 'edit' } as ContactFormDialogData,
    });

    dialogRef.afterClosed().subscribe((result: UpdatePhonebookContactDTO | { openExisting: PhonebookContactVM } | null) => {
      // Switch to editing a different contact from the duplicate banner.
      if (result && (result as any).openExisting) {
        this.openEditDialog((result as any).openExisting);
        return;
      }
      if (result) {
        this.phonebookService.update(contact.id, result as UpdatePhonebookContactDTO).subscribe({
          next: (updated) => {
            this.toastService.success('Contact updated');
            // Update in local list
            const idx = this.contacts.findIndex((c) => c.id === contact.id);
            if (idx >= 0) this.contacts[idx] = updated;
            // Update detail view if showing
            if (this.selectedContact?.id === contact.id) {
              this.selectedContact = updated;
            }
            this.loadCounts();
          },
          error: () => this.toastService.error('Failed to update contact'),
        });
      }
    });
  }

  openDeleteDialog(contact: PhonebookContactVM): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '420px',
      panelClass: 'bc-dialog-panel',
      autoFocus: false,
      data: { contact } as DeleteConfirmDialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.phonebookService.delete(contact.id).subscribe({
          next: () => {
            this.toastService.success('Contact deleted');
            this.contacts = this.contacts.filter((c) => c.id !== contact.id);
            this.total--;
            if (this.selectedContact?.id === contact.id) {
              this.backToList();
            }
            this.loadCounts();
          },
          error: () => this.toastService.error('Failed to delete contact'),
        });
      }
    });
  }

  // ═══════════════════════════════════
  // Quick Actions
  // ═══════════════════════════════════

  /** Strip a phone string to digits and leading +. */
  cleanPhone(phone: string | null | undefined): string {
    if (!phone) return '';
    return String(phone).replace(/[^\d+]/g, '');
  }

  /** Digits-only (no +), for WhatsApp wa.me links. */
  private digitsOnly(phone: string | null | undefined): string {
    if (!phone) return '';
    return String(phone).replace(/\D/g, '');
  }

  call3CX(phone: string | null | undefined): void {
    const cleaned = this.cleanPhone(phone);
    if (cleaned) window.location.href = `tel:${cleaned}`;
  }

  callWhatsApp(phone: string | null | undefined): void {
    const digits = this.digitsOnly(phone);
    if (digits) window.open(`https://wa.me/${digits}`, '_blank');
  }

  callTelegram(phone: string | null | undefined): void {
    const cleaned = this.cleanPhone(phone);
    if (cleaned) window.open(`https://t.me/${cleaned}`, '_blank');
  }

  emailContact(contact: PhonebookContactVM): void {
    if (contact.email) {
      window.open(`mailto:${contact.email}`, '_self');
    }
  }

  shareContact(contact: PhonebookContactVM, channel: 'clipboard' | 'email' | 'whatsapp' | 'telegram'): void {
    const text = this.buildContactText(contact);
    switch (channel) {
      case 'clipboard':
        navigator.clipboard.writeText(text).then(
          () => this.toastService.success('Contact copied to clipboard'),
          () => this.toastService.error('Failed to copy'),
        );
        break;
      case 'email':
        const name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
        const subject = encodeURIComponent(`Contact details: ${name}`);
        const body = encodeURIComponent(text);
        window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'telegram':
        window.open(`https://t.me/share/url?url=&text=${encodeURIComponent(text)}`, '_blank');
        break;
    }
  }

  private buildContactText(c: PhonebookContactVM): string {
    const lines: string[] = [];
    const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    if (name) lines.push(name);
    if (c.company) lines.push(`Company: ${c.company}`);
    if (c.jobTitle) lines.push(`Title: ${c.jobTitle}`);
    if (c.mobilePhone) lines.push(`Mobile: ${c.mobilePhone}`);
    if (c.workPhone) lines.push(`Work: ${c.workPhone}`);
    if (c.email) lines.push(`Email: ${c.email}`);
    if (c.tic) lines.push(`TIC: ${c.tic}`);
    if (c.vatNumber) lines.push(`VAT: ${c.vatNumber}`);
    if (c.address || c.city || c.country) {
      const addr = [c.address, c.city, c.country].filter(v => v?.trim()).join(', ');
      if (addr) lines.push(`Address: ${addr}`);
    }
    return lines.join('\n');
  }

  // ═══════════════════════════════════
  // Sidebar Actions
  // ═══════════════════════════════════

  close(): void {
    this.sidebarService.close();
  }

  onBackdropClick(): void {
    this.close();
  }

  // ═══════════════════════════════════
  // Helpers
  // ═══════════════════════════════════

  getInitials(contact: PhonebookContactVM): string {
    const f = contact.firstName?.[0] || '';
    const l = contact.lastName?.[0] || '';
    return (f + l).toUpperCase() || '?';
  }

  getAvatarHue(contact: PhonebookContactVM): number {
    const hues = [210, 180, 260, 330, 150, 30, 0, 45, 290, 120];
    return hues[(contact.firstName || '').charCodeAt(0) % hues.length];
  }

  getSubtitle(contact: PhonebookContactVM): string {
    if (contact.jobTitle && contact.company) {
      return `${contact.jobTitle} · ${contact.company}`;
    }
    return contact.company || contact.jobTitle || contact.email || '';
  }

  trackByContact(_: number, c: PhonebookContactVM): number {
    return c.id;
  }

  // Infinite scroll handler
  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      this.loadMore();
    }
  }

  // ═══════════════════════════════════
  // Type Colors
  // ═══════════════════════════════════

  private typeColorMap: Record<string, { dot: string; bg: string; border: string; text: string }> = {
    Work:            { dot: '#4296ff', bg: '#e8f1ff', border: '#b8d4ff', text: '#1d5cbf' },
    Client:          { dot: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46' },
    Employee:        { dot: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd', text: '#5b21b6' },
    Vendor:          { dot: '#f59e0b', bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
    Consultant:      { dot: '#06b6d4', bg: '#ecfeff', border: '#a5f3fc', text: '#155e75' },
    Legal:           { dot: '#6366f1', bg: '#eef2ff', border: '#c7d2fe', text: '#3730a3' },
    Bank:            { dot: '#14b8a6', bg: '#f0fdfa', border: '#99f6e4', text: '#115e59' },
    Government:      { dot: '#f97316', bg: '#fff7ed', border: '#fed7aa', text: '#9a3412' },
    'Family Member': { dot: '#ec4899', bg: '#fdf2f8', border: '#fbcfe8', text: '#9d174d' },
    General:         { dot: '#9ba3b0', bg: '#f8f9fb', border: '#e2e5eb', text: '#5f6775' },
  };

  getTypeColor(type: string): string {
    return this.typeColorMap[type]?.dot || '#9ba3b0';
  }

  getTypeBadgeBg(type: string): string {
    return this.typeColorMap[type]?.bg || '#f8f9fb';
  }

  getTypeBadgeBorder(type: string): string {
    return this.typeColorMap[type]?.border || '#e2e5eb';
  }

  getTypeBadgeText(type: string): string {
    return this.typeColorMap[type]?.text || '#5f6775';
  }

  /** Material icon name for each preferred-contact channel. */
  contactMethodIcon(value: string): string {
    switch (value) {
      case 'Phone':     return 'phone';
      case 'WhatsApp':  return 'chat';
      case 'SMS':       return 'sms';
      case 'Email':     return 'email';
      case 'LinkedIn':  return 'work';
      case 'Telegram':  return 'send';
      case 'Signal':    return 'shield';
      case 'In person': return 'handshake';
      default:          return 'more_horiz';
    }
  }
}
